import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Bell, Check, X } from 'lucide-react'
import { notificationService, Notification } from '../services/notificationService'

interface NotificationDropdownProps {
  notifications?: Notification[]
  unreadCount?: number
}

export default function NotificationDropdown({ notifications: propNotifications, unreadCount: propUnreadCount }: NotificationDropdownProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>(propNotifications || [])
  const [unreadCount, setUnreadCount] = useState(propUnreadCount || 0)
  const [isLoading, setIsLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Update unread count when prop changes
  useEffect(() => {
    if (propUnreadCount !== undefined) {
      setUnreadCount(propUnreadCount)
    }
  }, [propUnreadCount])

  // Fetch notifications when dropdown opens
  const fetchNotifications = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await notificationService.getNotifications()
      if (response.success && response.notifications) {
        setNotifications(response.notifications)
        const calculatedUnreadCount = response.notifications.filter(n => !n.read).length
        setUnreadCount(response.unreadCount ?? calculatedUnreadCount)
      }
    } catch (error) {
      // Error handling - notifications are optional
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching notifications:', error)
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen && !propNotifications) {
      fetchNotifications()
    }
  }, [isOpen, propNotifications, fetchNotifications])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleMarkAsRead = async (notificationId: string) => {
    const result = await notificationService.markAsRead(notificationId)
    if (result.success) {
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    }
  }

  const handleMarkAllAsRead = async () => {
    const result = await notificationService.markAllAsRead()
    if (result.success) {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
      setUnreadCount(0)
    }
  }

  const handleClearAll = async () => {
    const result = await notificationService.clearAll()
    if (result.success) {
      setNotifications([])
      setUnreadCount(0)
    }
  }

  // Calculate display count: use prop if provided, otherwise use state
  const displayUnreadCount = propUnreadCount !== undefined ? propUnreadCount : unreadCount

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center gap-2 px-4 py-2 text-gray-900 bg-white hover:bg-gray-50 transition-colors rounded-lg border-2 border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        aria-label={t('notifications.bellAriaLabel', 'התראות')}
        aria-expanded={isOpen}
      >
        <span className="text-sm font-medium">{t('notifications.title', 'התראות')}</span>
        <Bell className="h-5 w-5 text-gray-900" />
        {displayUnreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full">
            {displayUnreadCount > 9 ? '9+' : displayUnreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {t('notifications.title', 'התראות')}
            </h3>
            <div className="flex items-center gap-2">
              {displayUnreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {t('notifications.markAllRead', 'סמן הכל כנקרא')}
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label={t('notifications.close', 'סגור')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">
                <p>{t('common.loading', 'טוען...')}</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>{t('notifications.empty', 'אין התראות חדשות')}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                      !notification.read ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => {
                      if (notification.link) {
                        window.location.href = notification.link
                      }
                      if (!notification.read) {
                        handleMarkAsRead(notification.id)
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                        !notification.read ? 'bg-primary-500' : 'bg-gray-300'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className={`text-sm font-medium ${
                            !notification.read ? 'text-gray-900' : 'text-gray-700'
                          }`}>
                            {notification.title}
                          </h4>
                          {!notification.read && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleMarkAsRead(notification.id)
                              }}
                              className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                              aria-label={t('notifications.markAsRead', 'סמן כנקרא')}
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(notification.createdAt).toLocaleDateString('he-IL', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-gray-200 p-3">
              <button
                onClick={handleClearAll}
                className="w-full text-sm text-center text-gray-600 hover:text-gray-900 py-2"
              >
                {t('notifications.clearAll', 'נקה הכל')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
